import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, PencilIcon } from 'lucide-react';
import { useFormState, FormData } from '../hooks/useFormState';
import { TCPAConsent } from '../components/TCPAConsent';
import { educationLevels, degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
interface LocationState {
  degreeInterest: string;
  areaOfStudy: string;
  specialization: string;
}
export function ExpandedFormPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  // Redirect if no state
  useEffect(() => {
    if (!state) {
      navigate('/', {
        replace: true
      });
    }
    window.scrollTo(0, 0);
  }, [state, navigate]);
  const {
    formData,
    errors,
    updateFormData,
    validateExpandedForm
  } = useFormState(state ? {
    degreeInterest: state.degreeInterest,
    areaOfStudy: state.areaOfStudy,
    specialization: state.specialization
  } : undefined);
  if (!state) return null;
  // Resolve labels for the summary bar
  const degreeName = degreeInterestOptions.find(d => d.value === state.degreeInterest)?.type || '';
  const focusName = focusOptions.find(f => f.value === state.areaOfStudy)?.type || '';
  const specName = specializationOptions.find(s => s.value === state.specialization)?.type || '';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateExpandedForm()) {
      // TODO: Send formData to matching API here
      console.log('Form submitted:', formData);
      navigate('/results', {
        state: {
          formData
        }
      });
    }
  };
  const selectClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg bg-white appearance-none cursor-pointer ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg shadow-sm ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const radioGroupClasses = (field: keyof FormData, value: string) => `flex-1 cursor-pointer border rounded-lg py-3 px-4 text-center text-sm font-medium transition-all duration-150 ${formData[field] === value ? 'border-navy bg-navy/5 text-navy ring-1 ring-navy' : 'border-gray-300 text-gray-600 hover:border-navy/40'}`;
  return <main className="flex-grow bg-light min-h-screen" data-id="element-469">
      {/* Top bar with selections summary */}
      <div className="bg-navy text-white" data-id="element-470">
        <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-471">
          <div className="flex items-center gap-2 text-sm" data-id="element-472">
            <ShieldCheckIcon className="w-4 h-4 text-gold flex-shrink-0" data-id="element-473" />
            <span className="text-gray-300" data-id="element-474">Your selections:</span>
            <span className="font-semibold" data-id="element-475">{degreeName}</span>
            <span className="text-gray-400" data-id="element-476">›</span>
            <span className="font-semibold" data-id="element-477">{focusName}</span>
            <span className="text-gray-400" data-id="element-478">›</span>
            <span className="font-semibold text-gold" data-id="element-479">{specName}</span>
          </div>
          <Link to="/" className="flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors" data-id="element-480">
            <PencilIcon className="w-3 h-3" data-id="element-481" />
            Edit
          </Link>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="max-w-2xl mx-auto px-4 py-8 sm:py-12" data-id="element-482">
        {/* Progress */}
        <div className="mb-8" data-id="element-483">
          <div className="flex justify-between items-center mb-2" data-id="element-484">
            <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-485">
              Step 2 of 2
            </span>
            <span className="text-xs font-medium text-gray-500" data-id="element-486">
              Almost done!
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-487">
            <div className="h-full bg-gold rounded-full w-1/2 transition-all duration-500" data-id="element-488" />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-489">
          <div className="bg-navy px-6 py-4" data-id="element-490">
            <h1 className="text-white font-bold text-lg tracking-wide text-center" data-id="element-491">
              Complete Your Profile
            </h1>
            <p className="text-gray-300 text-xs text-center mt-1" data-id="element-492">
              Tell us a bit about yourself so we can find the best matches.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6" data-id="element-493">
            {/* Education Level */}
            <div data-id="element-494">
              <label htmlFor="currentEducation" className="block text-sm font-semibold text-gray-800 mb-1.5" data-id="element-495">
                Highest Level of Education Completed
              </label>
              <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses('currentEducation')} data-id="element-496">
                <option value="" data-id="element-497">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-498">
                    {level}
                  </option>)}
              </select>
              {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-499">
                  {errors.currentEducation}
                </p>}
            </div>

            {/* US Citizen */}
            <div data-id="element-500">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-501">
                Are you a U.S. Citizen?
              </label>
              <div className="flex gap-3" data-id="element-502">
                <div onClick={() => updateFormData('isUSCitizen', 'yes')} className={radioGroupClasses('isUSCitizen', 'yes')} role="radio" aria-checked={formData.isUSCitizen === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'yes')} data-id="element-503">
                  Yes
                </div>
                <div onClick={() => updateFormData('isUSCitizen', 'no')} className={radioGroupClasses('isUSCitizen', 'no')} role="radio" aria-checked={formData.isUSCitizen === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'no')} data-id="element-504">
                  No
                </div>
              </div>
              {errors.isUSCitizen && <p className="mt-1 text-xs text-red-600" data-id="element-505">
                  {errors.isUSCitizen}
                </p>}
            </div>

            {/* Military */}
            <div data-id="element-506">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-507">
                U.S. Military Affiliation?
              </label>
              <div className="flex gap-3" data-id="element-508">
                <div onClick={() => updateFormData('isMilitary', 'yes')} className={radioGroupClasses('isMilitary', 'yes')} role="radio" aria-checked={formData.isMilitary === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'yes')} data-id="element-509">
                  Yes
                </div>
                <div onClick={() => updateFormData('isMilitary', 'no')} className={radioGroupClasses('isMilitary', 'no')} role="radio" aria-checked={formData.isMilitary === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'no')} data-id="element-510">
                  No
                </div>
              </div>
              {errors.isMilitary && <p className="mt-1 text-xs text-red-600" data-id="element-511">{errors.isMilitary}</p>}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 pt-6" data-id="element-512">
              <h3 className="text-sm font-semibold text-gray-800 mb-4" data-id="element-513">
                Contact Information
              </h3>
            </div>

            {/* Name Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-514">
              <div data-id="element-515">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-516">
                  First Name
                </label>
                <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses('firstName')} placeholder="Jane" data-id="element-517" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-518">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-519">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-520">
                  Last Name
                </label>
                <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses('lastName')} placeholder="Doe" data-id="element-521" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-522">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div data-id="element-523">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-524">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses('email')} placeholder="jane.doe@example.com" data-id="element-525" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-526">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div data-id="element-527">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-528">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', e.target.value)} className={inputClasses('phone')} placeholder="(555) 123-4567" data-id="element-529" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-530">{errors.phone}</p>}
            </div>

            {/* Address + Zip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-531">
              <div className="sm:col-span-2" data-id="element-532">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-533">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal" data-id="element-534">(optional)</span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-535" />
              </div>
              <div data-id="element-536">
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-537">
                  Zip Code
                </label>
                <input type="text" id="zipCode" value={formData.zipCode} onChange={e => updateFormData('zipCode', e.target.value)} className={inputClasses('zipCode')} placeholder="12345" maxLength={5} data-id="element-538" />
                {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-539">{errors.zipCode}</p>}
              </div>
            </div>

            {/* TCPA */}
            <TCPAConsent checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} data-id="element-540" />

            {/* Submit */}
            <button type="submit" className="w-full bg-crimson hover:bg-crimson-dark text-white font-bold py-4 px-6 rounded-lg text-base tracking-wide uppercase transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-crimson shadow-md" data-id="element-541">
              Get My Results
            </button>

            {/* Security note */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-2" data-id="element-542">
              <LockIcon className="w-3.5 h-3.5" data-id="element-543" />
              Your information is secure and will never be sold.
            </div>
          </form>
        </div>
      </motion.div>
    </main>;
}